import { z } from "zod";

export const AddDecibelAccountSchema = z.object({
  accountId: z.number().int().positive(),
  walletAddress: z.string().min(1),
  status: z.string(),
});
export type AddDecibelAccountDto = z.infer<typeof AddDecibelAccountSchema>;

/**
 * Chain from which the user's wallet originates. Used to compute the Aptos
 * primary account address — native Aptos wallets are used directly, EVM and
 * Solana wallets are resolved to an Aptos DAA (AIP-113) address.
 */
export const DecibelWalletChainSchema = z.enum(["aptos", "evm", "solana"]);
export type DecibelWalletChain = z.infer<typeof DecibelWalletChainSchema>;

const EVM_ADDRESS_REGEX = /^0x[0-9a-fA-F]{40}$/;
const APTOS_ADDRESS_REGEX = /^0x[0-9a-fA-F]{64}$/;
const SOLANA_ADDRESS_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

/**
 * Body of `POST /v1/exchange-account/decibel/delegation-prepare`.
 *
 * `walletAddress` is the SOURCE address (EVM `0x...` 40-hex, Solana base58,
 * or native Aptos `0x...` 64-hex). The backend derives the Aptos primary
 * address from this via AIP-113 DAA for EVM/Solana.
 */
export const PrepareDecibelDelegationBodySchema = z
  .object({
    walletAddress: z.string().min(1),
    walletChain: DecibelWalletChainSchema,
  })
  .superRefine((val, ctx) => {
    const { walletAddress, walletChain } = val;
    if (walletChain === "evm" && !EVM_ADDRESS_REGEX.test(walletAddress)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["walletAddress"],
        message: "EVM wallet address must be 0x + 40 hex chars",
      });
    } else if (walletChain === "aptos" && !APTOS_ADDRESS_REGEX.test(walletAddress)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["walletAddress"],
        message: "Aptos wallet address must be 0x + 64 hex chars",
      });
    } else if (walletChain === "solana" && !SOLANA_ADDRESS_REGEX.test(walletAddress)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["walletAddress"],
        message: "Solana wallet address must be base58 (32-44 chars)",
      });
    }
  });
export type PrepareDecibelDelegationBodyDto = z.infer<typeof PrepareDecibelDelegationBodySchema>;

/**
 * Human-readable description of the Move entry function the user is being asked
 * to sign. Displayed in the confirm UI; the authoritative bytes live in
 * `transactionBytesHex` (the wallet signs those exact bytes).
 */
export const DecibelDelegationTransactionPayloadSchema = z.object({
  function: z.string().min(1),
  typeArguments: z.array(z.string()),
  functionArguments: z.array(z.unknown()),
});
export type DecibelDelegationTransactionPayload = z.infer<typeof DecibelDelegationTransactionPayloadSchema>;

/**
 * Discriminator for each tx the user must sign during onboarding. UI uses this
 * to label the prompts (e.g. "Step 1 of 2: authorize agent wallet").
 */
export const DecibelPreparedTxKindSchema = z.enum(["DELEGATION", "BUILDER_FEE_APPROVAL"]);
export type DecibelPreparedTxKind = z.infer<typeof DecibelPreparedTxKindSchema>;

/**
 * One transaction in the onboarding sequence. Order matters — the user signs
 * them in array order and authorize submits them in the same order. Both share
 * the same Aptos sender; the backend pins `accountSequenceNumber` on tx[1] so
 * digests stay stable.
 */
export const DecibelPreparedTxSchema = z.object({
  kind: DecibelPreparedTxKindSchema,
  /**
   * BCS-serialized `SimpleTransaction` bytes (hex, with or without 0x prefix).
   * Single source of truth for the digest the user signs — do NOT rebuild.
   */
  transactionBytesHex: z.string().min(1),
  transactionPayload: DecibelDelegationTransactionPayloadSchema,
});
export type DecibelPreparedTxDto = z.infer<typeof DecibelPreparedTxSchema>;

/**
 * Result of `POST /v1/exchange-account/decibel/delegation-prepare`.
 *
 * The backend builds two `SimpleTransaction`s once (delegate trading + approve
 * builder fee), BCS-serializes each, and returns them under `transactions` in
 * sign order. The client MUST deserialize these exact bytes (via
 * `SimpleTransaction.deserialize(new Deserializer(bytes))`) and pass the
 * resulting tx to the wallet adapter's `signTransaction` — any client-side
 * rebuild re-derives `sequence_number` / `gas_unit_price` /
 * `expiration_timestamp_secs` from live chain state + wall clock, producing a
 * different digest than what authorize submits and a guaranteed
 * `INVALID_SIGNATURE` from the fullnode. The API wallet key stays server-side.
 */
export const PrepareDecibelDelegationResultSchema = z.object({
  prepareId: z.uuid(),
  transactions: z.array(DecibelPreparedTxSchema).min(1),
  withFeePayer: z.literal(true),
  subaccountAddress: z.string().min(1),
  aptosPrimary: z.string().min(1),
});
export type PrepareDecibelDelegationResultDto = z.infer<typeof PrepareDecibelDelegationResultSchema>;

/**
 * Body of `POST /v1/exchange-account/decibel/delegation-authorize`.
 *
 * Called once per prepared tx, in the SAME order as `transactions` from the
 * prepare result. `senderAuthenticator` is the raw `AccountAuthenticator` BCS
 * bytes returned by the wallet-adapter's `signTransaction` call for that step
 * (hex-encoded, 0x-prefixed). `stepIndex` (0-based) identifies which prepared
 * tx is being authorized.
 *
 * We DON'T authorize all txs in one shot because Petra simulates every tx
 * before signing; the second tx carries `sequence_number = first.seq + 1` and
 * its simulation would fail with `SEQUENCE_NUMBER_TOO_NEW` until the first tx
 * is committed on-chain. So the client signs + authorizes each step, waits
 * for on-chain inclusion, then signs the next — making each simulation valid.
 *
 * The backend deserializes the cached pre-built `SimpleTransaction` bytes
 * (same bytes the client signed against), attaches the sender auth, and
 * submits via Decibel's fee-payer gas station relay — the user's wallet
 * (potentially a DAA-derived EVM/Solana address with 0 APT) never pays gas.
 */
export const AuthorizeDecibelDelegationBodySchema = z.object({
  prepareId: z.uuid(),
  stepIndex: z.number().int().min(0),
  senderAuthenticator: z.string().regex(/^0x[0-9a-fA-F]+$/, "Must be a 0x-prefixed hex string"),
});
export type AuthorizeDecibelDelegationBodyDto = z.infer<typeof AuthorizeDecibelDelegationBodySchema>;

/**
 * Response from the authorize endpoint. Discriminated on `isFinal`:
 *   - `false` — intermediate step submitted (delegation committed, builder-fee
 *     still pending); client should sign the next tx.
 *   - `true`  — final step; credentials persisted, account fields populated.
 */
export const AuthorizeDecibelDelegationResultSchema = z.discriminatedUnion("isFinal", [
  z.object({ isFinal: z.literal(false) }),
  z.object({
    isFinal: z.literal(true),
    accountId: z.number().int().positive(),
    walletAddress: z.string(),
    status: z.string(),
  }),
]);
export type AuthorizeDecibelDelegationResultDto = z.infer<typeof AuthorizeDecibelDelegationResultSchema>;
